import {Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography} from "@mui/material"
import {useCallback, useEffect, useMemo, useState} from "react" // Imported useMemo
import {useForm, useWatch} from "react-hook-form";
import {AsyncSelect} from "src/components/forms";
import {z} from "src/lib";
import {MensajeFields, SistemaFields} from "../types";
import {useNavigate} from "react-router-dom";
import {zodResolver} from "@hookform/resolvers/zod";
import {EntityWithIdAndDescriptionFields, EntityWithIdFields, QueryStatus} from "src/types";
import {SistemaPrincipalFields} from "src/types/workflow/cache";
import {useSistemas} from "../api";
import {useSistemaPrincipal} from "../../../hooks/useSistemaPrincipal";
import {HttpSystems} from "../../../http/workflow/httpSystems";


const SchemaFields = {
    SistemaPrincipal: MensajeFields.CodigoSistemaPrincipal,
    Sistema: MensajeFields.CodigoSistema
} as const

const schema = z.object({
    [SchemaFields.SistemaPrincipal]: z.number(),
    [SchemaFields.Sistema]: z.number().optional()
})

type Schema = z.infer<typeof schema>

export const NuevoMensaje = () => {
    const [openDialog, setOpenDialog] = useState<boolean>(false);

    const { control, handleSubmit, resetField } = useForm<Schema>({
        resolver: zodResolver(schema),
        mode: 'onChange'
    });

    const sistemaPrincipalId = useWatch({
        control,
        name: SchemaFields.SistemaPrincipal,
    });

    useEffect(() => {
        resetField(SchemaFields.Sistema);
    }, [sistemaPrincipalId, resetField]);

    const getOpcionesSistemaPrincipal = useCallback(async () => {
        const data = await HttpSystems.getSystemPrincipalsWithCreationPermission();
        
        return data.map(opcion => ({
            id: opcion[EntityWithIdFields.Id],
            descripcion: opcion[EntityWithIdAndDescriptionFields.Description],
            icon: <Avatar src={opcion[SistemaPrincipalFields.SrcIcono]} sx={{ height: '20px', width: '20px' }} />
        }));
    }, []);

    const { status, sistemas } = useSistemas(false, sistemaPrincipalId);

    const getOpcionesSistema = useCallback(async () => {
        return sistemas || [];
    }, [sistemas]);

    const navigation = useNavigate();

    const crearNuevo = (data: Schema) => {
        const sistemaId = data[SchemaFields.Sistema];
        const sistema = sistemas?.find(s => s.id === sistemaId);
        const sistemaTipo = sistema?.[SistemaFields.SistemaTipo];

        navigation(`/operacion/nuevo?sistemaPrincipal=${data[SchemaFields.SistemaPrincipal]}&sistemaTipo=${sistemaTipo}&sistema=${sistemaId}`);
    }

    return (
        <>
            <Stack>
                <Button variant='contained' onClick={() => setOpenDialog(true)}>Nuevo</Button>
            </Stack>
            <Dialog
                open={openDialog}
                maxWidth='md'
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2.5,
                        maxWidth: 1080,
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle sx={{ px: { xs: 3, sm: 4 }, pt: 4, pb: 1, fontWeight: 600 }}>
                    Nueva operación
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 3, sm: 4 }, pt: '8px !important', pb: 4 }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                            gap: 2.5,
                            alignItems: 'start'
                        }}
                    >
                        <Typography variant='body2' color='text.secondary' sx={{ alignSelf: 'center' }}>
                            Seleccioná el país y el circuito de la operación.
                        </Typography>
                        <Box sx={{ width: { xs: '100%', sm: 450 }, maxWidth: '100%', justifySelf: 'end' }}>
                            <AsyncSelect
                                control={control}
                                name={SchemaFields.SistemaPrincipal}
                                label='País'
                                loadOptions={getOpcionesSistemaPrincipal}
                                fullWidth
                                selectNotNulleable
                                onlyOpenOnArrow
                            />
                        </Box>
                        <AsyncSelect
                            key={sistemaPrincipalId}
                            control={control}
                            name={SchemaFields.Sistema}
                            label='Circuito'
                            loadOptions={getOpcionesSistema}
                            disabled={!sistemaPrincipalId || status === QueryStatus.Loading}
                            fullWidth
                            selectNotNulleable
                            onlyOpenOnArrow
                            sx={{ gridColumn: { sm: '1 / -1' } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: { xs: 3, sm: 4 },
                        py: 2.5,
                        gap: 1,
                        borderTop: 1,
                        borderColor: 'divider',
                        bgcolor: 'action.hover'
                    }}
                >
                    <Button variant='text' color='primary' sx={{ minWidth: 100 }} onClick={() => setOpenDialog(false)}>
                        Cancelar
                    </Button>
                    <Button variant='contained' color='primary' sx={{ minWidth: 100 }} onClick={handleSubmit(crearNuevo)}>
                        Crear
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
